import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, Clock } from "lucide-react";

/** Stripe return page — polls the payment status up to 10 attempts (2s each). */
export default function PaymentReturn() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const bookingId = params.get("booking_id");

  const [state, setState] = useState("polling");
  const [err, setErr] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); setErr("Session manquante"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.status === "expired") { setState("expired"); return; }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 10) { setState("timeout"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || e.message);
        setState("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="mx-auto flex max-w-lg flex-col items-center gap-5 px-6 py-24 text-center" data-testid="payment-return">
      {state === "polling" && (
        <>
          <div className="grid h-14 w-14 place-items-center rounded-full bg-primary/10 text-primary">
            <Clock className="h-6 w-6 animate-pulse" />
          </div>
          <h1 className="font-[Outfit] text-2xl">Vérification du paiement…</h1>
          <p className="text-sm text-muted-foreground">Ne fermez pas cette page. Nous confirmons votre paiement auprès de Stripe.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <div className="grid h-14 w-14 place-items-center rounded-full bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <h1 className="font-[Outfit] text-2xl">Paiement confirmé</h1>
          <p className="text-sm text-muted-foreground">La course est payée. Vous pouvez suivre son avancement en temps réel.</p>
          <Button className="rounded-full bg-primary" onClick={() => nav(`/dashboard/hotel/pay/${bookingId}`)} data-testid="return-see-booking">
            Voir le suivi de la course
          </Button>
        </>
      )}
      {state === "expired" && (
        <>
          <div className="grid h-14 w-14 place-items-center rounded-full bg-destructive/15 text-destructive">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <h1 className="font-[Outfit] text-2xl">Session expirée</h1>
          <p className="text-sm text-muted-foreground">Votre session de paiement a expiré. Veuillez réessayer.</p>
          <Button className="rounded-full" onClick={() => nav(`/dashboard/hotel/pay/${bookingId}`)}>Réessayer</Button>
        </>
      )}
      {state === "timeout" && (
        <>
          <div className="grid h-14 w-14 place-items-center rounded-full bg-accent/15 text-accent">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <h1 className="font-[Outfit] text-2xl">Paiement en cours de traitement</h1>
          <p className="text-sm text-muted-foreground">Le statut n'est pas encore confirmé. Vérifiez votre email ou revenez à l'historique.</p>
          <Button className="rounded-full bg-primary" onClick={() => nav("/dashboard/hotel/history")}>Voir l'historique</Button>
        </>
      )}
      {state === "error" && (
        <>
          <div className="grid h-14 w-14 place-items-center rounded-full bg-destructive/15 text-destructive">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <h1 className="font-[Outfit] text-2xl">Erreur</h1>
          <p className="text-sm text-destructive">{err}</p>
          <Button onClick={() => nav("/dashboard/hotel/history")}>Retour</Button>
        </>
      )}
    </div>
  );
}
